// `when` filters for the map/list (ARCHITECTURE §5), computed in the city's timezone.
// An event matches a window when it overlaps it: `start_at < window.end AND end_at > window.start`.
// Callers additionally always require `end_at > now`, so past parts of a window never match.

export const WHEN_FILTERS = ['now', 'today', 'tomorrow', 'weekend'] as const;
export type WhenFilter = (typeof WHEN_FILTERS)[number];

export interface TimeWindow {
	start: Date;
	end: Date;
}

/** "now" also includes events starting within this many minutes. */
export const NOW_LOOKAHEAD_MINUTES = 60;

interface LocalDate {
	year: number;
	month: number;
	day: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
	let f = formatters.get(timeZone);
	if (f === undefined) {
		f = new Intl.DateTimeFormat('en-US', {
			timeZone,
			hourCycle: 'h23',
			year: 'numeric',
			month: 'numeric',
			day: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric',
		});
		formatters.set(timeZone, f);
	}
	return f;
}

/** Wall-clock parts of `instant` in `timeZone`. */
function wallClock(instant: Date, timeZone: string) {
	const parts = Object.fromEntries(
		formatter(timeZone)
			.formatToParts(instant)
			.filter((part) => part.type !== 'literal')
			.map((part) => [part.type, Number(part.value)]),
	) as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', number>;
	return parts;
}

/** Offset of `timeZone` from UTC at `instant`, in ms (e.g. +3h for Kyiv in summer). */
function offsetMs(instant: Date, timeZone: string): number {
	const p = wallClock(instant, timeZone);
	const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
	return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** The UTC instant of local midnight at the start of `date` in `timeZone` (DST-safe). */
export function localMidnight(date: LocalDate, timeZone: string): Date {
	const naive = Date.UTC(date.year, date.month - 1, date.day);
	// Two passes settle the offset even when a DST switch lies between `naive` and the answer.
	let guess = naive - offsetMs(new Date(naive), timeZone);
	guess = naive - offsetMs(new Date(guess), timeZone);
	return new Date(guess);
}

/**
 * The UTC instant of a wall-clock `YYYY-MM-DD` + `HH:mm` in `timeZone` (DST-safe, same two
 * passes as `localMidnight`). Used by forms that ask for a local date and time.
 */
export function zonedDateTimeToUtc(date: string, time: string, timeZone: string): Date {
	const [year = 0, month = 0, day = 0] = date.split('-').map(Number);
	const [hour = 0, minute = 0] = time.split(':').map(Number);
	const naive = Date.UTC(year, month - 1, day, hour, minute);
	let guess = naive - offsetMs(new Date(naive), timeZone);
	guess = naive - offsetMs(new Date(guess), timeZone);
	return new Date(guess);
}

/** `{ date: 'YYYY-MM-DD', time: 'HH:mm' }` of `instant` on the wall clock of `timeZone`. */
export function utcToZonedDateTime(
	instant: Date,
	timeZone: string,
): { date: string; time: string } {
	const p = wallClock(instant, timeZone);
	const two = (n: number) => String(n).padStart(2, '0');
	return {
		date: `${String(p.year)}-${two(p.month)}-${two(p.day)}`,
		time: `${two(p.hour)}:${two(p.minute)}`,
	};
}

function addDays(date: LocalDate, days: number): LocalDate {
	const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
	return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function dayWindow(date: LocalDate, timeZone: string): TimeWindow {
	return { start: localMidnight(date, timeZone), end: localMidnight(addDays(date, 1), timeZone) };
}

/**
 * Window for a `when` filter:
 * - `now`: in progress or starting within 60 min → [now, now + 60 min)
 * - `today` / `tomorrow`: the local calendar day
 * - `weekend`: local Saturday 00:00 → Monday 00:00 (the current one on Sat/Sun)
 */
export function timeWindowFor(when: WhenFilter, timeZone: string, now: Date): TimeWindow {
	if (when === 'now') {
		return { start: now, end: new Date(now.getTime() + NOW_LOOKAHEAD_MINUTES * 60_000) };
	}

	const p = wallClock(now, timeZone);
	const today: LocalDate = { year: p.year, month: p.month, day: p.day };
	if (when === 'today') return dayWindow(today, timeZone);
	if (when === 'tomorrow') return dayWindow(addDays(today, 1), timeZone);

	// 0 = Sunday … 6 = Saturday, for the local date.
	const weekday = new Date(Date.UTC(today.year, today.month - 1, today.day)).getUTCDay();
	const daysToSaturday = weekday === 0 ? -1 : 6 - weekday;
	const saturday = addDays(today, daysToSaturday);
	return {
		start: localMidnight(saturday, timeZone),
		end: localMidnight(addDays(saturday, 2), timeZone),
	};
}
